import Modal from "../../components/ui/Modal";
import { formatDateTime } from "../../helpers/formatDateTime";
import { getCommsAge, resolveSiteIdentifierRegistryId } from "./commsSite";
import type { CommsSite } from "./commsSite";
import { getNoteAuthorText } from "./commsNotes";
import type { SiteNote } from "./commsNotes";
import type { NoteHistory } from "./useCommsNotes";

function SiteFields({ site }: { site: CommsSite }) {
    return (
        <>
            <div><strong>Site Name:</strong> {site.siteName || "—"}</div>
            <div><strong>Site Identifier:</strong> {site.siteIdentifier || "—"}</div>
            <div><strong>Region:</strong> {site.region || "—"}</div>
            <div><strong>System Type:</strong> {site.systemType || "—"}</div>
            <div><strong>Status:</strong> {site.status || site.statusDescription || "—"}</div>
            <div><strong>Last Heartbeat:</strong> {formatDateTime(site.lastHeartbeatReceived)}</div>
            <div><strong>Age:</strong> {getCommsAge(site)}</div>
            <div><strong>Offline Reasons:</strong> {site.offlineReasons?.join(", ") || "—"}</div>
            <div><strong>Registry ID:</strong> {resolveSiteIdentifierRegistryId(site) ?? "—"}</div>
        </>
    );
}

export function NoteList({ notes, users, prefix = "" }: { notes: SiteNote[]; users: Record<string, string>; prefix?: string }) {
    return (
        <ul className="previous-messages">
            {notes.map((note, index) => <li key={`${note.createdAt ?? "note"}-${index}`}>
                <div>{note.message}</div>
                <small>{`${prefix}${getNoteAuthorText(note, users)} - `}{note.createdAt ? formatDateTime(note.createdAt) : "Unknown time"}</small>
            </li>)}
        </ul>
    );
}

interface Props {
    site: CommsSite;
    message: string;
    history: NoteHistory;
    users: Record<string, string>;
    offline: boolean;
    onView: (site: CommsSite) => void;
    onAddMessage: (site: CommsSite) => void;
}

export function CommsSiteDetailsPanel({ site, message, history, users, offline, onView, onAddMessage }: Props) {
    const latest = history.notes[0];
    const previous = history.notes.slice(1, 6);
    const id = resolveSiteIdentifierRegistryId(site);
    return (
        <div>
            <div className="admin-requests--panel-heading">
                <h4 className="heading heading__title--building mt-0">{site.siteIdentifier || site.siteName || `Site ${site.id}`}</h4>
            </div>
            <div className="comms-details__panel">
                <SiteFields site={site} />
                <div><strong>Latest Message:</strong></div>
                <div className="admin-requests__panel--reason">
                    {message !== "—" ? <>
                        <div>{message}</div>
                        {latest && <small>By {getNoteAuthorText(latest, users)}{latest.createdAt ? ` - ${formatDateTime(latest.createdAt)}` : ""}</small>}
                    </> : "No message available."}
                </div>
                <div><strong>Previous Messages:</strong></div>
                <div>
                    {history.loading && <div>Loading previous messages...</div>}
                    {!history.loading && history.error && <div>{history.error}</div>}
                    {!history.loading && !history.error && !previous.length && <div>No previous messages.</div>}
                    {!history.loading && !history.error && !!previous.length && <NoteList notes={previous} users={users} prefix="By " />}
                </div>
            </div>
            <div className="admin-requests__buttons">
                <button className="btn btn-view" onClick={() => onView(site)}>View Details</button>
                {offline && <button className="btn btn-edit" disabled={id === null} onClick={() => onAddMessage(site)}>Add Message</button>}
            </div>
        </div>
    );
}

export function CommsSiteViewDialog({ site, message, onClose }: { site: CommsSite; message: string; onClose: () => void }) {
    return (
        <Modal isOpen title={`Site ${site.siteIdentifier || site.siteName || ""}`} onConfirm={onClose} onCancel={onClose}
            content={<div className="modal__body"><SiteFields site={site} />{message !== "—" && <div><strong>Message:</strong> {message}</div>}</div>} />
    );
}
